import React, { useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/mode-text";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";

const DELIMITERS = [
  { label: "Comma (,)", value: "," },
  { label: "Semicolon (;)", value: ";" },
  { label: "Pipe (|)", value: "|" },
];

export default function JSONToCSVTool() {
  const [jsonInput, setJsonInput] = useState(`{
  "standings": [
    { "team": "Pakistan", "played": 5, "won": 5, "lost": 0, "points": 10, "nrr": "+1.8" },
    { "team": "India", "played": 5, "won": 4, "lost": 1, "points": 8, "nrr": "+1.2" },
    { "team": "Australia", "played": 5, "won": 3, "lost": 2, "points": 6, "nrr": "+0.7" },
    { "team": "England", "played": 5, "won": 2, "lost": 3, "points": 4, "nrr": "+0.1" },
    { "team": "South Africa", "played": 5, "won": 1, "lost": 4, "points": 2, "nrr": "-0.5" },
    { "team": "New Zealand", "played": 5, "won": 0, "lost": 5, "points": 0, "nrr": "-1.3" }
  ]
}`);
  const [csvOutput, setCsvOutput] = useState("");
  const [delimiter, setDelimiter] = useState(",");
  const [includeHeaders, setIncludeHeaders] = useState(true);

  // Download CSV logic
  const handleDownload = () => {
    let filename = "output.csv";
    try {
      const data = JSON.parse(jsonInput);
      if (!Array.isArray(data) && data && typeof data === "object") {
        for (const key in data) {
          if (
            Array.isArray(data[key]) &&
            data[key].length > 0 &&
            typeof data[key][0] === "object"
          ) {
            filename = key + "_output.csv";
            break;
          }
        }
      }
    } catch {}
    const blob = new Blob([csvOutput], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  // JSON to CSV conversion logic
  const handleConvert = () => {
    let data;
    try {
      data = JSON.parse(jsonInput);
    } catch (err) {
      setCsvOutput(`❌ Invalid JSON: ${err.message}`);
      return;
    }
    // Accept root array, root object, or root object with first array of objects inside
    let arr = null;
    if (Array.isArray(data)) {
      arr = data;
    } else if (data && typeof data === "object") {
      // If it's a plain object, treat as single row
      if (Object.keys(data).length > 0 && !Array.isArray(data)) {
        arr = [data];
      }
      // Or, find first array of objects property
      for (const key in data) {
        if (
          Array.isArray(data[key]) &&
          data[key].length > 0 &&
          typeof data[key][0] === "object"
        ) {
          arr = data[key];
          break;
        }
      }
    }
    if (!arr || arr.length === 0 || typeof arr[0] !== "object") {
      setCsvOutput(
        "❌ Input must be or contain an array of objects or a single object",
      );
      return;
    }
    // Collect all unique keys for headers
    const allKeys = Array.from(
      arr.reduce((set, row) => {
        Object.keys(row || {}).forEach((k) => set.add(k));
        return set;
      }, new Set()),
    );
    // Helper to escape CSV values, handling nested objects/arrays as JSON strings
    const escape = (val) => {
      if (val == null) return "";
      let str;
      if (typeof val === "object") {
        str = JSON.stringify(val);
      } else {
        str = String(val);
      }
      if (str.includes('"') || str.includes(delimiter) || str.includes("\n")) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };
    let csv = [];
    if (includeHeaders) {
      csv.push(allKeys.map(escape).join(delimiter));
    }
    for (const row of arr) {
      csv.push(allKeys.map((k) => escape(row[k])).join(delimiter));
    }
    setCsvOutput(csv.join("\n"));
  };

  const handleClear = () => {
    setJsonInput("");
    setCsvOutput("");
  };

  return (
    <div style={{ padding: "1rem", fontFamily: "Arial" }}>
      {/* Separator bar with controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 16,
          borderBottom: "1px solid #ddd",
          paddingBottom: 8,
        }}
      >
        <span style={{ fontWeight: 500, marginRight: 16 }}>Delimiter:</span>
        <select
          value={delimiter}
          onChange={(e) => setDelimiter(e.target.value)}
          style={{ marginRight: 24, padding: "2px 8px" }}
        >
          {DELIMITERS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input
            type="checkbox"
            checked={includeHeaders}
            onChange={(e) => setIncludeHeaders(e.target.checked)}
            style={{ marginRight: 4 }}
          />
          Include headers
        </label>
      </div>

      {/* Editors in two columns */}
      <div className="container mt-3 p-3 bg-light rounded shadow">
        <div style={{ display: "flex", gap: 16, paddingTop: 10 }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                marginBottom: 13,
                fontWeight: 500,
                color: "#333",
              }}
            >
              JSON Input
            </div>
            <AceEditor
              mode="json"
              theme="monokai"
              name="json_input_editor"
              value={jsonInput}
              onChange={setJsonInput}
              fontSize={14}
              width="100%"
              height="350px"
              showPrintMargin={true}
              showGutter={true}
              highlightActiveLine={true}
              useWorker={false}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 500, color: "#333" }}>CSV Output</span>
              <span>
                <button
                  type="button"
                  title="Copy CSV"
                  className="btn btn-secondary btn-sm me-2"
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(csvOutput);
                    } else {
                      alert(
                        "Copy to clipboard is not supported in this browser.",
                      );
                    }
                  }}
                  disabled={!csvOutput}
                >
                  Copy
                </button>
                <button
                  type="button"
                  title="Download CSV"
                  className="btn btn-secondary btn-sm"
                  onClick={handleDownload}
                  disabled={!csvOutput}
                >
                  Download
                </button>
              </span>
            </div>
            <AceEditor
              mode="text"
              theme="monokai"
              name="csv_output_editor"
              value={csvOutput}
              onChange={setCsvOutput}
              fontSize={14}
              width="100%"
              height="350px"
              showPrintMargin={true}
              showGutter={true}
              highlightActiveLine={true}
              readOnly={false}
              useWorker={false}
              setOptions={{
                enableBasicAutocompletion: false,
                enableLiveAutocompletion: false,
                enableSnippets: false,
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
          </div>
        </div>

        {/* Buttons below editors */}
        <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
          <button onClick={handleConvert} className="btn btn-primary">
            Convert to CSV
          </button>
          <button onClick={handleClear} className="btn btn-secondary">
            Clear
          </button>
        </div>
      </div>

      <div
        className="page-info"
        style={{
          flexBasis: "100%",
          marginTop: 32,
          background: "#f8f9fa",
          borderRadius: 8,
          padding: 20,
          border: "1px solid #eee",
        }}
      >
        <h3 style={{ color: "#4908c2", marginBottom: 10 }}>
          How JSON to CSV Conversion Works
        </h3>
        <p style={{ color: "#333", fontSize: 15, margin: 0 }}>
          This tool transforms your JSON array into a CSV (Comma-Separated
          Values) file. Each item in the array is converted into a row, while
          each key becomes a column header. It’s a quick and easy way to move
          structured JSON data into spreadsheet programs like Excel or Google
          Sheets for further analysis or sharing.
        </p>
      </div>

      <div
        className="page-info"
        style={{
          flexBasis: "100%",
          marginTop: 32,
          background: "#f8f9fa",
          borderRadius: 8,
          padding: 20,
          border: "1px solid #eee",
        }}
      >
        <h3 style={{ color: "#4908c2", marginBottom: 10 }}>
          What JSON Structure Works Best?
        </h3>
        <p style={{ color: "#333", fontSize: 15, margin: 0 }}>
          The ideal input is an array of flat objects:
          <pre>
            <code>
              {`[
    { "team": "Australia", "played": 5, "won": 3, "lost": 2, "points": 6, "nrr": "+0.7" },
    { "team": "England", "played": 5, "won": 2, "lost": 3, "points": 4, "nrr": "+0.1" },
]`}
            </code>
          </pre>
        </p>

        <p style={{ color: "#333", fontSize: 15, margin: 0 }}>
          Or an object containing an array:
          <pre>
            <code>
              {`{
  "standings": [
    { "team": "Australia", "played": 5, "won": 3, "lost": 2, "points": 6, "nrr": "+0.7" },
    { "team": "England", "played": 5, "won": 2, "lost": 3, "points": 4, "nrr": "+0.1" },
  ]
}`}
            </code>
          </pre>
        </p>
      </div>

      <div
        className="page-info"
        style={{
          flexBasis: "100%",
          marginTop: 32,
          background: "#f8f9fa",
          borderRadius: 8,
          padding: 20,
          border: "1px solid #eee",
        }}
      >
        <h3 style={{ color: "#4908c2", marginBottom: 10 }}>
          Handling Nested Objects
        </h3>
        <p style={{ color: "#333", fontSize: 15, margin: 0 }}>
          When your JSON contains nested objects or arrays, they're converted to
          JSON strings in the CSV output. For example:
          <pre>
            <code>
              {`{"team":"Australia","played":5,"won":3,"lost":2,"points":6,"nrr":"+0.7","coach":{"name":"John Smith","country":"Australia"}}`}
            </code>
          </pre>
        </p>
      </div>
    </div>
  );
}
